import { useEffect, useRef, useCallback } from "react";

/**
 * 自定义Interval Hook
 * 提供可控的定时执行功能，支持动态启停和间隔调整
 *
 * @param {Function} callback - 定时执行的回调函数
 * @param {number|null} delay - 执行间隔（毫秒），null表示暂停
 * @param {boolean} immediate - 是否立即执行第一次（默认false）
 * @returns {Object} 返回控制方法
 */
const useInterval = (callback, delay, immediate = false) => {
    const savedCallback = useRef();
    const intervalId = useRef(null);

    // 保存最新的callback
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // 手动触发执行
    const trigger = useCallback(() => {
        if (savedCallback.current) {
            savedCallback.current();
        }
    }, []);

    // 清除定时器
    const clear = useCallback(() => {
        if (intervalId.current !== null) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, []);

    // 设置定时器
    useEffect(() => {
        // 如果delay为null，清除定时器
        if (delay === null) {
            clear();
            return;
        }

        // 立即执行第一次（如果设置）
        if (immediate && savedCallback.current) {
            savedCallback.current();
        }

        // 设置定时器
        intervalId.current = setInterval(() => {
            if (savedCallback.current) {
                savedCallback.current();
            }
        }, delay);

        // 清理函数
        return clear;
    }, [delay, immediate, clear]);

    return {
        trigger, // 手动触发执行
        clear, // 清除定时器
    };
};

export default useInterval;
